import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, ChevronRight, Copy, Check, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Head, Loader, EmptyBox } from "./shared";

const INPUT = "w-full bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none";
const STATUSES = ["Not started", "Requested", "Provisioning", "Live"];
const statusColor = (s) => s === "Live" ? "text-green-400" : s === "Provisioning" ? "text-gold" : s === "Requested" ? "text-blue-300" : "text-gray-500";

function embedSnippet(widgetId) {
  return `<!-- ATF WebWorks — AI Concierge (AnyChat) -->
<script>
(function(){var id="contactus-jssdk";if(document.getElementById(id))return;
var fjs=document.getElementsByTagName("script")[0];var js=document.createElement("script");
js.id=id;js.src="https://api.anychat.one/widget/${widgetId}?r="+encodeURIComponent(window.location);
fjs.parentNode.insertBefore(js,fjs);})();
</script>`;
}

export function AIConcierge() {
  const [projects, setProjects] = useState(null);
  const [selId, setSelId] = useState("");
  const [stats, setStats] = useState(null);
  const [offer, setOffer] = useState(null);
  const [savingOffer, setSavingOffer] = useState(false);

  const load = () => api.get("/admin/concierge").then((r) => setProjects(r.data.projects || [])).catch(() => setProjects([]));
  useEffect(() => {
    load();
    api.get("/admin/concierge/analytics").then((r) => setStats(r.data)).catch(() => {});
    api.get("/concierge/offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);

  const saveOffer = async () => {
    setSavingOffer(true);
    try {
      const { data } = await api.put("/admin/concierge/offer", { active: !!offer.active, price: Number(offer.price) || 0, slots: Number(offer.slots) || 0 });
      setOffer(data);
      toast.success("Founding offer saved.");
    } catch (e) { toast.error("Could not save offer"); } finally { setSavingOffer(false); }
  };

  if (projects === null) return <Loader />;
  const selected = projects.find((p) => p.id === selId);

  return (
    <div data-testid="admin-ai-concierge">
      <Head title="AI Concierge" desc="Sell each client their own AnyChat AI assistant. Set up the assistant in your AnyChat Agency dashboard, then track provisioning here and copy the embed snippet to drop onto their site. Powered by your AnyChat Agency plan." />

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="concierge-analytics">
          {[
            ["Upsell views", stats.views],
            ["Pay clicks", stats.clicks],
            ["Click rate", `${stats.click_rate}%`],
            ["Purchases", stats.purchases],
          ].map(([label, val]) => (
            <div key={label} className="bg-navy-800 border border-white/10 p-3">
              <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
              <div className="text-2xl font-bold text-gold mt-1">{val}</div>
            </div>
          ))}
        </div>
      )}

      {offer && (
        <div className="bg-navy-800 border border-white/10 p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="concierge-offer">
          <div>
            <div className="text-sm text-white font-semibold">Founding offer</div>
            <div className="text-xs text-gray-400 mt-0.5">Discount the Launch Bundle for your first clients to create urgency & earn case studies. {offer.remaining} of {offer.slots} spots left.</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-white cursor-pointer">
              <input type="checkbox" checked={!!offer.active} onChange={(e) => setOffer((o) => ({ ...o, active: e.target.checked }))} data-testid="offer-active" className="accent-gold w-4 h-4" /> Active
            </label>
            <span className="text-xs text-gray-400">$</span>
            <input type="number" min="0" value={offer.price} onChange={(e) => setOffer((o) => ({ ...o, price: e.target.value }))} data-testid="offer-price" className="w-20 bg-navy border border-white/15 text-white text-sm px-2 py-1.5 focus:border-gold focus:outline-none" />
            <span className="text-xs text-gray-400">for first</span>
            <input type="number" min="0" value={offer.slots} onChange={(e) => setOffer((o) => ({ ...o, slots: e.target.value }))} data-testid="offer-slots" className="w-16 bg-navy border border-white/15 text-white text-sm px-2 py-1.5 focus:border-gold focus:outline-none" />
            <button onClick={saveOffer} disabled={savingOffer} data-testid="offer-save" className="text-sm bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 disabled:opacity-60">{savingOffer ? "…" : "Save"}</button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        <div className="space-y-1.5" data-testid="concierge-project-list">
          {projects.length === 0 && <EmptyBox text="No projects yet." />}
          {projects.map((p) => {
            const st = p.concierge?.status || "Not started";
            return (
              <button key={p.id} onClick={() => setSelId(p.id)} data-testid={`concierge-project-${p.id}`}
                className={`w-full text-left p-3 border transition-colors ${selId === p.id ? "border-gold/60 bg-gold/5" : "border-white/10 bg-navy-800 hover:border-white/25"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-white truncate">{p.org_name || p.email || "Untitled"}</span>
                  <ChevronRight size={14} className="text-gray-500 shrink-0" />
                </div>
                <div className={`text-[11px] mt-0.5 ${statusColor(st)}`}>● {st}</div>
              </button>
            );
          })}
        </div>
        <div>
          {!selected && <EmptyBox text="Select a client on the left to manage their AI Concierge." />}
          {selected && <Panel key={selected.id} project={selected} onSaved={load} />}
        </div>
      </div>
    </div>
  );
}

function Panel({ project, onSaved }) {
  const c = project.concierge || {};
  const [status, setStatus] = useState(c.status || "Not started");
  const [widgetId, setWidgetId] = useState(c.widget_id || "");
  const [notes, setNotes] = useState(c.notes || "");
  const [demoUrl, setDemoUrl] = useState(c.demo_url || "");
  const [questions, setQuestions] = useState(c.questions_this_month || 0);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/projects/${project.id}/concierge`, { status, widget_id: widgetId.trim(), notes, demo_url: demoUrl.trim(), questions_this_month: Number(questions) || 0 });
      toast.success("Concierge status saved.");
      onSaved?.();
    } catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(embedSnippet(widgetId.trim())); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Embed snippet copied."); }
    catch (e) { toast.error("Copy failed — select and copy manually."); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-navy-800 border border-white/10 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bot size={16} className="text-gold" />
          <div className="text-sm text-white font-semibold">{project.org_name || project.email}</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Provisioning status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="concierge-status" className={INPUT}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">AnyChat Widget ID</label>
            <input value={widgetId} onChange={(e) => setWidgetId(e.target.value)} data-testid="concierge-widget-id" className={INPUT} placeholder="e.g. 51a40459-8d09-3425-a575-af0a5a8e2543" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs text-gray-400 mb-1 block">Live site URL <span className="text-gray-600">(where the assistant is embedded — shown to the client)</span></label>
          <input value={demoUrl} onChange={(e) => setDemoUrl(e.target.value)} data-testid="concierge-demo-url" className={INPUT} placeholder="https://theirbusiness.com" />
        </div>
        <div className="mt-3">
          <label className="text-xs text-gray-400 mb-1 block">Questions answered this month <span className="text-gray-600">(shown to the client as ROI — update monthly)</span></label>
          <input type="number" min="0" value={questions} onChange={(e) => setQuestions(e.target.value)} data-testid="concierge-questions" className={INPUT} placeholder="0" />
        </div>
        <div className="mt-3">
          <label className="text-xs text-gray-400 mb-1 block">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="concierge-notes" className={INPUT + " resize-y"} placeholder="Knowledge base source, tone, hand-off email, etc." />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={save} disabled={saving} data-testid="concierge-save" className="bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          <a href="https://app.anychat.one" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gold">Open AnyChat dashboard <ExternalLink size={12} /></a>
        </div>
      </div>

      <div className="bg-navy-800 border border-white/10 p-4" data-testid="concierge-embed">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-white font-semibold">Website embed snippet</div>
          <button onClick={copy} disabled={!widgetId.trim()} data-testid="concierge-copy" className="inline-flex items-center gap-1.5 text-xs text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/10 disabled:opacity-40">
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        {widgetId.trim()
          ? <pre className="text-[11px] text-gray-300 bg-navy border border-white/10 p-3 overflow-x-auto whitespace-pre-wrap" data-testid="concierge-snippet">{embedSnippet(widgetId.trim())}</pre>
          : <p className="text-xs text-gray-500">Enter the client's AnyChat Widget ID above to generate their embed snippet, then paste it into their website build.</p>}
      </div>
    </div>
  );
}
